import React, { useEffect, useState } from "react";
import { api, fmtSDG } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X, Pause, Printer, Trash2, FileText, User, MapPin, Phone, Smartphone, Tag, Percent, Bike, Pencil } from "lucide-react";
import { toast } from "sonner";
import { printReceipt } from "@/lib/printer";
import PrinterStatusCard from "@/components/PrinterStatusCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_LABELS = {
    pending: "pending",
    confirmed: "confirmed",
    delivered: "delivered",
    on_hold: "on_hold",
    paid: "paid",
    rejected: "rejected",
    out_for_delivery: "out_for_delivery",
};

export default function CashierHome() {
    const { t, lang, user } = useApp();
    const [orders, setOrders] = useState([]);
    const [tab, setTab] = useState("pending");
    const [detail, setDetail] = useState(null);
    const [confirmKitchen, setConfirmKitchen] = useState(null); // for delete item flow
    const [discountOrder, setDiscountOrder] = useState(null);  // order being discounted
    const [discountMode, setDiscountMode] = useState("amount"); // "amount" | "percent"
    const [discountValue, setDiscountValue] = useState("");
    const [discountReason, setDiscountReason] = useState("");
    const [deliveryFeeOrder, setDeliveryFeeOrder] = useState(null);
    const [deliveryFeeValue, setDeliveryFeeValue] = useState("");
    const canDiscount = ["owner", "manager", "cashier"].includes(user?.role);
    const isManager = ["owner", "manager"].includes(user?.role);

    const load = async () => {
        const r = await api.get("/orders");
        setOrders(r.data);
    };

    useEffect(() => {
        load();
        const i = setInterval(load, 4000);
        return () => clearInterval(i);
    }, []);

    const filtered = orders.filter((o) =>
        tab === "pending" ? o.status === "pending"
            : tab === "active" ? ["confirmed", "delivered", "out_for_delivery"].includes(o.status)
            : tab === "hold" ? o.status === "on_hold"
            : true
    );

    const confirmPrint = async (o) => {
        try {
            await api.post(`/orders/${o.id}/confirm`);
            toast.success(t("order_confirmed"));
            const settings = (await api.get("/settings")).data;
            await printReceipt({ order: { ...o, status: "confirmed", cashier_name: user.name }, settings, lang });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    const reject = async (o) => {
        if (!window.confirm(t("confirm_delete") + " — " + t("reject"))) return;
        await api.post(`/orders/${o.id}/reject`, { reason: "" });
        toast.info(t("order_rejected"));
        await load();
    };

    const hold = async (o) => {
        await api.post(`/orders/${o.id}/hold`);
        await load();
    };

    const close = async (o) => {
        await api.post(`/orders/${o.id}/close`);
        toast.success(t("order_closed"));
        setDetail(null);
        await load();
    };

    const openDiscount = (o) => {
        setDiscountOrder(o);
        const isPercent = o.discount_percent != null && o.discount_percent > 0;
        setDiscountMode(isPercent ? "percent" : "amount");
        setDiscountValue(isPercent ? String(o.discount_percent) : String(o.discount_amount || ""));
        setDiscountReason(o.discount_reason || "");
    };

    const applyDiscount = async () => {
        if (!discountOrder) return;
        const v = Number(discountValue);
        const body = { reason: discountReason };
        if (discountMode === "percent") body.percent = v || 0;
        else body.amount = v || 0;
        try {
            const { data } = await api.post(`/orders/${discountOrder.id}/discount`, body);
            toast.success(`${t("discount_applied")} · ${data.new_total ? fmtSDG(data.new_total, lang) : ""}`);
            setDiscountOrder(null);
            setDiscountValue("");
            setDiscountReason("");
            // refresh detail dialog if open
            const updated = (await api.get(`/orders/${discountOrder.id}`)).data;
            if (detail?.id === updated.id) setDetail(updated);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    const removeDiscount = async () => {
        if (!discountOrder) return;
        try {
            await api.post(`/orders/${discountOrder.id}/discount`, { amount: 0, reason: "" });
            toast.success(t("discount_removed"));
            setDiscountOrder(null);
            const updated = (await api.get(`/orders/${discountOrder.id}`)).data;
            if (detail?.id === updated.id) setDetail(updated);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    const openDeliveryFee = (o) => {
        setDeliveryFeeOrder(o);
        setDeliveryFeeValue(String(o.delivery_fee || ""));
    };

    const applyDeliveryFee = async () => {
        if (!deliveryFeeOrder) return;
        const fee = Number(deliveryFeeValue);
        if (isNaN(fee) || fee < 0) {
            toast.error(t("invalid_amount"));
            return;
        }
        try {
            const { data } = await api.post(`/orders/${deliveryFeeOrder.id}/delivery-fee`, { fee });
            toast.success(`${t("delivery_fee")} · ${fmtSDG(data.new_total, lang)}`);
            setDeliveryFeeOrder(null);
            setDeliveryFeeValue("");
            const updated = (await api.get(`/orders/${deliveryFeeOrder.id}`)).data;
            if (detail?.id === updated.id) setDetail(updated);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    const removeItem = async (o, idx) => {
        if (o.kitchen_started || confirmKitchen?.kitchenStarted === true) {
            toast.error(t("kitchen_yes_cancel"));
            return;
        }
        // ask: has kitchen started?
        setConfirmKitchen({ order: o, idx });
    };

    const performRemove = async (kitchenStarted) => {
        const { order: o, idx } = confirmKitchen;
        if (kitchenStarted) {
            await api.post(`/orders/${o.id}/kitchen-start`);
            toast.info(t("kitchen_yes_cancel"));
            setConfirmKitchen(null);
            await load();
            return;
        }
        const newItems = o.items.filter((_, i) => i !== idx);
        await api.patch(`/orders/${o.id}`, { items: newItems });
        toast.success(t("removed"));
        setConfirmKitchen(null);
        const r = await api.get(`/orders/${o.id}`);
        setDetail(r.data);
        await load();
    };

    const reprintReceipt = async (o) => {
        const settings = (await api.get("/settings")).data;
        await printReceipt({ order: o, settings, lang });
    };

    return (
        <div className="space-y-4" data-testid="cashier-home">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-sm text-muted-foreground">{t("cashier")} · {user?.name}</div>
                    <h1 className={`${lang === "ar" ? "font-ar" : "font-display-en italic"} text-3xl`}>{t("live_orders")}</h1>
                </div>
                <PrinterStatusCard compact />
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="pending" data-testid="cashier-tab-pending">{t("pending")} ({orders.filter(o => o.status === "pending").length})</TabsTrigger>
                    <TabsTrigger value="active" data-testid="cashier-tab-active">{t("confirmed")} ({orders.filter(o => ["confirmed", "delivered"].includes(o.status)).length})</TabsTrigger>
                    <TabsTrigger value="hold" data-testid="cashier-tab-hold">{t("on_hold")}</TabsTrigger>
                    <TabsTrigger value="all" data-testid="cashier-tab-all">{t("all_orders")}</TabsTrigger>
                </TabsList>

                <TabsContent value={tab} className="mt-4">
                    {filtered.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">{t("no_pending")}</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {filtered.map((o) => (
                                <Card key={o.id} className={`grain bg-card/70 ${o.source === "online" ? "border-primary/40" : ""}`} data-testid={`order-card-${o.id}`}>
                                    <CardContent className="p-4 space-y-3">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <div className="text-lg font-medium">{t("order_no")} #{o.order_no}</div>
                                                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${o.source === "online" ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>
                                                        {o.source === "online" ? <Smartphone className="h-3 w-3" /> : <User className="h-3 w-3" />}
                                                        {o.source === "online" ? t("from_online") : t("from_waiter")}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-0.5">
                                                    {o.order_type === "delivery"
                                                        ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{t("delivery")}</span>
                                                        : o.order_type === "pickup"
                                                            ? t("pickup")
                                                            : <>{t("table")} {o.table_number}</>}
                                                    {" · "}{o.source === "online" ? o.customer_name : o.waiter_name}
                                                </div>
                                                {o.source === "online" && o.customer_phone && (
                                                    <div className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1"><Phone className="h-3 w-3" />{o.customer_phone}</div>
                                                )}
                                                {o.order_type === "delivery" && o.delivery_address && (
                                                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{o.delivery_address}</div>
                                                )}
                                                {o.order_type === "delivery" && o.payment_method && (
                                                    <div className="mt-1.5">
                                                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${o.payment_method === "bankak" ? "bg-blue-500/15 text-blue-500 border border-blue-500/30" : "bg-emerald-500/15 text-emerald-500 border border-emerald-500/30"}`}>
                                                            {t("payment_method")}: {t(o.payment_method)}
                                                        </span>
                                                    </div>
                                                )}
                                                {o.assigned_driver_name && (
                                                    <div className="mt-1.5 text-[11px] inline-flex items-center gap-1 text-amber-400" data-testid="assigned-driver">
                                                        <Smartphone className="h-3 w-3" />
                                                        {t("driver")}: {o.assigned_driver_name}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-primary/15 text-primary shrink-0">
                                                {t(STATUS_LABELS[o.status] || o.status)}
                                            </div>
                                        </div>
                                        <div className="text-sm space-y-1 max-h-32 overflow-y-auto">
                                            {o.items.map((it, i) => (
                                                <div key={i} className="flex justify-between">
                                                    <span><span className="text-muted-foreground tabular-nums me-2">{it.qty}×</span>{lang === "ar" ? it.name_ar : it.name_en}</span>
                                                    <span className="tabular-nums text-muted-foreground">{fmtSDG(it.price * it.qty, lang)}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-between pt-2 border-t border-border">
                                            <span className="text-xs text-muted-foreground">{t("total")}</span>
                                            <span className="text-primary text-lg font-medium tabular-nums">{fmtSDG(o.total, lang)}</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Button size="sm" variant="outline" onClick={() => setDetail(o)} data-testid={`view-order-${o.id}`}><FileText className="h-3.5 w-3.5 me-1" />{t("view")}</Button>
                                            {o.status === "pending" && (
                                                <>
                                                    <Button size="sm" onClick={() => confirmPrint(o)} data-testid={`confirm-order-${o.id}`}><Check className="h-3.5 w-3.5 me-1" />{t("confirm_print")}</Button>
                                                    <Button size="sm" variant="outline" onClick={() => hold(o)} data-testid={`hold-order-${o.id}`}><Pause className="h-3.5 w-3.5 me-1" />{t("hold")}</Button>
                                                    <Button size="sm" variant="ghost" onClick={() => reject(o)} data-testid={`reject-order-${o.id}`}><X className="h-3.5 w-3.5 me-1 text-destructive" />{t("reject")}</Button>
                                                </>
                                            )}
                                            {["confirmed", "delivered"].includes(o.status) && (
                                                <Button size="sm" onClick={() => close(o)} data-testid={`close-order-${o.id}`}>{t("close_session")}</Button>
                                            )}
                                            {o.status === "on_hold" && (
                                                <Button size="sm" onClick={() => confirmPrint(o)} data-testid={`resume-order-${o.id}`}>{t("confirm_print")}</Button>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Detail dialog */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent data-testid="order-detail-dialog" className="max-h-[90vh] overflow-y-auto">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle>{t("order_no")} #{detail.order_no} · {t("table")} {detail.table_number}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div className="text-xs text-muted-foreground">{detail.waiter_name} → {detail.cashier_name || "—"}</div>
                                <div className="rounded-xl border border-border divide-y divide-border">
                                    {detail.items.map((it, i) => (
                                        <div key={i} className="p-3 flex items-center gap-3" data-testid={`detail-item-${i}`}>
                                            <span className="text-muted-foreground tabular-nums">{it.qty}×</span>
                                            <span className="flex-1">{lang === "ar" ? it.name_ar : it.name_en}</span>
                                            <span className="tabular-nums">{fmtSDG(it.price * it.qty, lang)}</span>
                                            {detail.status !== "paid" && ["owner", "manager", "cashier"].includes(user.role) && (
                                                <Button size="icon" variant="ghost" onClick={() => removeItem(detail, i)} data-testid={`remove-item-${i}`}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-1.5 pt-3 border-t border-border">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">{t("subtotal")}</span>
                                        <span className="tabular-nums">{fmtSDG(detail.subtotal || detail.total, lang)}</span>
                                    </div>
                                    {detail.discount_amount > 0 && (
                                        <div className="flex justify-between text-sm" data-testid="detail-discount-row">
                                            <span className="text-emerald-400 flex items-center gap-1">
                                                <Tag className="h-3 w-3" />
                                                {t("discount")}
                                                {detail.discount_percent ? ` (${detail.discount_percent}%)` : ""}
                                                {detail.discount_reason ? ` · ${detail.discount_reason}` : ""}
                                            </span>
                                            <span className="text-emerald-400 tabular-nums">− {fmtSDG(detail.discount_amount, lang)}</span>
                                        </div>
                                    )}
                                    {detail.delivery_fee > 0 && (
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-muted-foreground flex items-center gap-1">
                                                <Bike className="h-3.5 w-3.5" />
                                                {t("delivery_fee")}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <span className="tabular-nums">{fmtSDG(detail.delivery_fee, lang)}</span>
                                                {detail.order_type === "delivery" && detail.status !== "paid" && canDiscount && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openDeliveryFee(detail)}
                                                        className="text-amber-400 hover:text-amber-300"
                                                        data-testid="edit-delivery-fee-btn"
                                                        title={t("edit")}
                                                    >
                                                        <Pencil className="h-3 w-3" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {detail.order_type === "delivery" && detail.status !== "paid" && canDiscount && detail.delivery_fee === 0 && (
                                        <button
                                            type="button"
                                            onClick={() => openDeliveryFee(detail)}
                                            className="w-full flex items-center justify-between text-sm text-amber-400 hover:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 transition-colors"
                                            data-testid="set-delivery-fee-btn"
                                        >
                                            <span className="flex items-center gap-2">
                                                <Bike className="h-4 w-4" />
                                                {t("set_delivery_fee")}
                                            </span>
                                            <Pencil className="h-3 w-3" />
                                        </button>
                                    )}
                                    {detail.tax > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">{t("tax")}</span>
                                            <span className="tabular-nums">{fmtSDG(detail.tax, lang)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between pt-1.5 border-t border-border">
                                        <span className="text-muted-foreground">{t("total")}</span>
                                        <span className="text-primary text-xl font-medium tabular-nums">{fmtSDG(detail.total, lang)}</span>
                                    </div>
                                </div>
                            </div>
                            <DialogFooter className="flex-wrap gap-2">
                                {canDiscount && detail.status !== "paid" && (
                                    <Button variant="outline" onClick={() => openDiscount(detail)} className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" data-testid="discount-btn">
                                        <Tag className="h-4 w-4 me-2" />
                                        {detail.discount_amount > 0 ? t("edit_discount") : t("add_discount")}
                                    </Button>
                                )}
                                <Button variant="outline" onClick={() => reprintReceipt(detail)} data-testid="reprint-btn"><Printer className="h-4 w-4 me-2" />{t("print_receipt")}</Button>
                                {["confirmed", "delivered"].includes(detail.status) && (
                                    <Button onClick={() => close(detail)} data-testid="detail-close-btn">{t("close_session")}</Button>
                                )}
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* kitchen start check */}
            <Dialog open={!!confirmKitchen} onOpenChange={(o) => !o && setConfirmKitchen(null)}>
                <DialogContent data-testid="kitchen-check-dialog">
                    <DialogHeader>
                        <DialogTitle>{t("kitchen_started_q")}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 pt-2">
                        <Button variant="outline" onClick={() => performRemove(true)} data-testid="kitchen-yes-btn">{t("kitchen_yes_cancel")}</Button>
                        <Button onClick={() => performRemove(false)} data-testid="kitchen-no-btn">{t("kitchen_no_delete")}</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Discount dialog — owner/manager only */}
            <Dialog open={!!discountOrder} onOpenChange={(o) => !o && setDiscountOrder(null)}>
                <DialogContent data-testid="discount-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Tag className="h-5 w-5 text-emerald-400" />
                            {t("discount")} · #{discountOrder?.order_no}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div className="text-sm text-muted-foreground bg-secondary/40 rounded-lg p-2.5 flex justify-between">
                            <span>{t("subtotal")}</span>
                            <span className="tabular-nums font-medium text-foreground">{fmtSDG(discountOrder?.subtotal || discountOrder?.total || 0, lang)}</span>
                        </div>
                        <div>
                            <Label className="text-xs mb-1.5 block">{t("discount_type")}</Label>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setDiscountMode("amount")}
                                    className={`px-3 py-2.5 rounded-xl text-sm border transition-all ${discountMode === "amount" ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}
                                    data-testid="discount-mode-amount"
                                >
                                    {t("amount_sdg")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDiscountMode("percent")}
                                    className={`px-3 py-2.5 rounded-xl text-sm border transition-all flex items-center justify-center gap-1 ${discountMode === "percent" ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}
                                    data-testid="discount-mode-percent"
                                >
                                    <Percent className="h-3.5 w-3.5" />
                                    {t("percent")}
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs mb-1.5 block">
                                {discountMode === "percent" ? t("percent_0_100") : t("amount_sdg")}
                            </Label>
                            <Input
                                type="number"
                                min="0"
                                max={discountMode === "percent" ? "100" : undefined}
                                value={discountValue}
                                onChange={(e) => setDiscountValue(e.target.value)}
                                placeholder={discountMode === "percent" ? "20" : "5000"}
                                className="text-xl h-14 text-center tabular-nums font-medium"
                                data-testid="discount-value-input"
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label className="text-xs mb-1.5 block">{t("reason_optional")}</Label>
                            <Input
                                value={discountReason}
                                onChange={(e) => setDiscountReason(e.target.value)}
                                placeholder={lang === "ar" ? "عرض رمضان، خصم VIP، ..." : "Ramadan promo, VIP, ..."}
                                data-testid="discount-reason-input"
                            />
                        </div>
                    </div>
                    <DialogFooter className="flex-wrap gap-2">
                        {discountOrder?.discount_amount > 0 && (
                            <Button variant="ghost" onClick={removeDiscount} className="text-destructive" data-testid="discount-remove">
                                <X className="h-4 w-4 me-1" />
                                {t("remove_discount")}
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => setDiscountOrder(null)}>{t("cancel")}</Button>
                        <Button onClick={applyDiscount} className="bg-emerald-600 hover:bg-emerald-700" data-testid="discount-apply">
                            <Check className="h-4 w-4 me-1" />
                            {t("apply")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Delivery fee dialog */}
            <Dialog open={!!deliveryFeeOrder} onOpenChange={(o) => !o && setDeliveryFeeOrder(null)}>
                <DialogContent data-testid="delivery-fee-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bike className="h-5 w-5 text-amber-400" />
                            {t("set_delivery_fee")} · #{deliveryFeeOrder?.order_no}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        {deliveryFeeOrder?.delivery_address && (
                            <div className="text-xs bg-secondary/40 rounded-lg p-2.5 flex items-start gap-2">
                                <MapPin className="h-3.5 w-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                                <span className="text-muted-foreground">{deliveryFeeOrder.delivery_address}</span>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs mb-1.5 block">{t("amount_sdg")}</Label>
                            <Input
                                type="number"
                                min="0"
                                value={deliveryFeeValue}
                                onChange={(e) => setDeliveryFeeValue(e.target.value)}
                                placeholder="3000"
                                className="text-xl h-14 text-center tabular-nums font-medium"
                                data-testid="delivery-fee-input"
                                autoFocus
                            />
                            <p className="text-[11px] text-muted-foreground mt-1.5">
                                {lang === "ar" ? "اكتب 0 لإلغاء الرسوم" : "Enter 0 to remove the fee"}
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeliveryFeeOrder(null)}>{t("cancel")}</Button>
                        <Button onClick={applyDeliveryFee} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="delivery-fee-apply">
                            <Check className="h-4 w-4 me-1" />
                            {t("apply")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
